import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketDrivenStatusUpdateRequest_Status } from '../../../../../pb/requests/v1/market_driven_status_update_request_pb';
import { activateMarketDrivenShipping } from '../../api/activateMarketDrivenShipping';

interface MarketDrivenShippingSwitchBackConfirmModalProps {
    id: string;
    modalRef: React.RefObject<HTMLElementTagNameMap['s-modal'] | null>;
    /** Called once the switch-back request has succeeded. */
    onConfirm: () => void;
}

// The "are you sure?" step before setting the ecom back to LEGACY (delivery profile) shipping, shown from
// MarketDrivenShippingSwitchedBackBanner's button. Same shape as MarketDrivenShippingMigrateConfirmModal:
// the request runs from here and only hands over to `onConfirm` once it succeeds; a failure keeps the
// modal open with an error banner instead.
export const MarketDrivenShippingSwitchBackConfirmModal: React.FC<MarketDrivenShippingSwitchBackConfirmModalProps> = ({
    id,
    modalRef,
    onConfirm,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isSwitching, setIsSwitching] = useState(false);
    const [hasError, setHasError] = useState(false);

    const handleConfirm = () => {
        setIsSwitching(true);
        setHasError(false);

        activateMarketDrivenShipping(MarketDrivenStatusUpdateRequest_Status.LEGACY)
            .then(() => onConfirm())
            .catch((requestError) => {
                console.error('Failed to switch back to delivery profiles', requestError);
                setHasError(true);
            })
            .finally(() => setIsSwitching(false));
    };

    return (
        <s-modal
            ref={modalRef}
            id={id}
            heading={t('MARKET_DRIVEN_SHIPPING_SWITCHED_BACK_BANNER.CONFIRM_MODAL.TITLE')}
            onShow={() => {
                setIsOpen(true);
                setHasError(false);
            }}
            onHide={() => setIsOpen(false)}
        >
            {isOpen && (
                <div className="flex flex-col gap-3">
                    {hasError && (
                        <s-banner
                            tone="critical"
                            heading={t('MARKET_DRIVEN_SHIPPING_SWITCHED_BACK_BANNER.CONFIRM_MODAL.ERROR.TITLE')}
                        >
                            <s-paragraph>
                                {t('MARKET_DRIVEN_SHIPPING_SWITCHED_BACK_BANNER.CONFIRM_MODAL.ERROR.DESCRIPTION')}
                            </s-paragraph>
                        </s-banner>
                    )}
                    <s-text>{t('MARKET_DRIVEN_SHIPPING_SWITCHED_BACK_BANNER.CONFIRM_MODAL.DESCRIPTION')}</s-text>
                </div>
            )}
            <s-button
                slot="primary-action"
                variant="primary"
                loading={isSwitching || undefined}
                onClick={handleConfirm}
            >
                {t('MARKET_DRIVEN_SHIPPING_SWITCHED_BACK_BANNER.CONFIRM_MODAL.CONFIRM_BUTTON')}
            </s-button>
            <s-button slot="secondary-actions" commandFor={id} command="--hide" disabled={isSwitching || undefined}>
                {t('BUTTON.CANCEL')}
            </s-button>
        </s-modal>
    );
};

export default MarketDrivenShippingSwitchBackConfirmModal;
